import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  listProducts,
  updateProduct,
  deleteProduct,
} from '../services/adminProductService';
import { Query } from '../lib/appwrite';
import { listStores } from '../services/adminStoreService';
import { getPlatformProductAnalytics } from '../services/adminAnalyticsService';
import TimeRangeSelector from '../components/TimeRangeSelector';
import AdminAuthService from '../services/adminAuthService';
import { logActivity } from '../services/adminActivityService';
import { can } from '../utils/adminPermissions';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { PRODUCTS_TABLE_ID } from '../lib/appwrite';
import './ProductsPage.css';

const ProductsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [adminRole, setAdminRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStore, setFilterStore] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [modal, setModal] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({ productName: '', productCategory: '', sellPrice: '', stockCount: '', unit: '' });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsTimeRange, setAnalyticsTimeRange] = useState('month');

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const data = await getPlatformProductAnalytics({ timeRange: analyticsTimeRange });
      setAnalyticsData(data);
    } catch {
      setAnalyticsData(null);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [analyticsTimeRange]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [productsRes, storesRes, role] = await Promise.all([
        listProducts([Query.limit(1000)]),
        listStores(),
        AdminAuthService.getAdminRole(),
      ]);
      setProducts(productsRes || []);
      setStores(storesRes || []);
      setAdminRole(role);
    } catch (err) {
      setError(err.message || t('products.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useAutoRefresh(loadData, { realtimeTables: [PRODUCTS_TABLE_ID] });

  useEffect(() => {
    if (activeTab === 'analytics') loadAnalytics();
  }, [activeTab, loadAnalytics]);

  const storeMap = stores.reduce((acc, s) => {
    acc[s.$id] = s.storeName || t('orders.unknown');
    return acc;
  }, {});

  const categories = [...new Set(products.map((p) => p.productCategory).filter(Boolean))].sort();

  const filteredProducts = products.filter((p) => {
    const matchSearch =
      !search ||
      (p.productName || '').toLowerCase().includes(search.toLowerCase()) ||
      (p.productId || '').toLowerCase().includes(search.toLowerCase());
    const matchStore = !filterStore || p.storeId === filterStore;
    const matchCategory = !filterCategory || p.productCategory === filterCategory;
    return matchSearch && matchStore && matchCategory;
  });

  const formatCurrency = (v) => {
    const n = parseFloat(v);
    if (isNaN(n)) return '—';
    return n.toLocaleString('en-TZ', { style: 'decimal' });
  };

  const openEdit = (product) => {
    setSelectedProduct(product);
    setFormData({
      productName: product.productName || '',
      productCategory: product.productCategory || '',
      sellPrice: String(product.sellPrice ?? ''),
      stockCount: String(product.stockCount ?? ''),
      unit: product.unit || '',
    });
    setModal('edit');
  };

  const openView = (product) => {
    navigate(`/dashboard/products/${product.$id}`);
  };

  const closeModal = () => {
    setModal(null);
    setSelectedProduct(null);
    setDeleteConfirm(null);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!selectedProduct?.$id) return;
    try {
      setSaving(true);
      setError(null);
      await updateProduct(selectedProduct.$id, {
        productName: formData.productName,
        productCategory: formData.productCategory || undefined,
        sellPrice: parseInt(formData.sellPrice, 10) || 0,
        stockCount: parseInt(formData.stockCount, 10) ?? 0,
        unit: formData.unit || undefined,
      });
      await logActivity({
        action: 'update',
        resourceType: 'product',
        resourceId: selectedProduct.$id,
        details: `Updated product: ${formData.productName || selectedProduct.productName}`,
      });
      closeModal();
      loadData();
    } catch (err) {
      setError(err.message || t('products.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm?.$id) return;
    try {
      setSaving(true);
      setError(null);
      await deleteProduct(deleteConfirm.$id);
      await logActivity({
        action: 'delete',
        resourceType: 'product',
        resourceId: deleteConfirm.$id,
        details: `Deleted product: ${deleteConfirm.productName || deleteConfirm.$id}`,
      });
      closeModal();
      setDeleteConfirm(null);
      loadData();
    } catch (err) {
      setError(err.message || t('products.deleteFailed'));
    } finally {
      setSaving(false);
    }
  };

  const canUpdate = adminRole && can(adminRole, 'update', 'products');
  const canDelete = adminRole && can(adminRole, 'delete', 'products');

  if (loading) {
    return (
      <div className="products-loading">
        <div className="dashboard-spinner" />
        <p>{t('products.loading')}</p>
      </div>
    );
  }

  return (
    <div className="products-page">
      <div className="products-header">
        <div>
          <h1 className="dashboard-title">{t('nav.products')}</h1>
          <p className="dashboard-subtitle">
            {t('products.subtitle')}
          </p>
        </div>
      </div>

      {error && (
        <div className="products-error" role="alert">
          {error}
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss">
            ×
          </button>
        </div>
      )}

      <nav className="products-tabs">
        <button
          type="button"
          className={`products-tab ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          {t('products.productList')}
        </button>
        <button
          type="button"
          className={`products-tab ${activeTab === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveTab('analytics')}
        >
          {t('nav.analytics')}
        </button>
      </nav>

      {activeTab === 'analytics' && (
        <div className="products-analytics">
          <div className="products-analytics-toolbar">
            <p className="products-analytics-desc">{t('products.analyticsDesc')}</p>
            <TimeRangeSelector value={analyticsTimeRange} onChange={setAnalyticsTimeRange} />
          </div>
          {analyticsLoading ? (
            <div className="products-analytics-loading">
              <div className="dashboard-spinner" />
              <p>{t('products.loadingAnalytics')}</p>
            </div>
          ) : analyticsData ? (
            <>
              <div className="products-analytics-cards">
                <div className="products-analytics-card">
                  <span className="products-analytics-value">{analyticsData.summary.totalProducts}</span>
                  <span className="products-analytics-label">{t('products.totalProducts')}</span>
                </div>
                <div className="products-analytics-card">
                  <span className="products-analytics-value">{analyticsData.summary.totalCategories}</span>
                  <span className="products-analytics-label">{t('products.categories')}</span>
                </div>
                <div className="products-analytics-card products-analytics-card-revenue">
                  <span className="products-analytics-value">{formatCurrency(analyticsData.summary.totalProductRevenue)}</span>
                  <span className="products-analytics-label">{t('orders.revenueTzs')}</span>
                </div>
                <div className="products-analytics-card">
                  <span className="products-analytics-value">{formatCurrency(analyticsData.summary.totalUnitsSold)}</span>
                  <span className="products-analytics-label">{t('dashboard.unitsSold')}</span>
                </div>
                <div className="products-analytics-card">
                  <span className="products-analytics-value">{analyticsData.summary.totalOrders}</span>
                  <span className="products-analytics-label">{t('nav.orders')}</span>
                </div>
                <div className="products-analytics-card products-analytics-card-warn">
                  <span className="products-analytics-value">{analyticsData.summary.lowStockCount}</span>
                  <span className="products-analytics-label">{t('dashboard.lowStock')}</span>
                </div>
                <div className="products-analytics-card products-analytics-card-danger">
                  <span className="products-analytics-value">{analyticsData.summary.outOfStockCount}</span>
                  <span className="products-analytics-label">{t('dashboard.outOfStock')}</span>
                </div>
              </div>

              <div className="products-analytics-charts">
                <div className="products-analytics-chart-box">
                  <h3 className="products-analytics-chart-title">{t('products.revenueOrders12Months')}</h3>
                  <ResponsiveContainer width="100%" height={240}>
                    <AreaChart data={analyticsData.byMonth.map((m) => ({ name: m.label, revenue: m.revenue, orders: m.orders }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#6b7280" />
                      <YAxis yAxisId="left" tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} stroke="#6b7280" />
                      <YAxis yAxisId="right" orientation="right" stroke="#6b7280" />
                      <Tooltip formatter={(v, name) => [name?.includes('Revenue') ? formatCurrency(v) : v, name]} />
                      <Legend />
                      <Area yAxisId="left" type="monotone" dataKey="revenue" name={t('orders.revenueTzs')} stroke="#039855" fill="#10b981" fillOpacity={0.4} strokeWidth={2} />
                      <Area yAxisId="right" type="monotone" dataKey="orders" name={t('nav.orders')} stroke="#f79009" fill="#f79009" fillOpacity={0.3} strokeWidth={2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="products-analytics-chart-box products-analytics-chart-pie">
                  <h3 className="products-analytics-chart-title">{t('products.revenueByCategory')}</h3>
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={analyticsData.byCategory.slice(0, 6).map((c) => ({ name: c.name, value: c.revenue }))}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {analyticsData.byCategory.slice(0, 6).map((_, i) => (
                          <Cell key={i} fill={['#039855', '#10b981', '#34d399', '#6ee7b7', '#047857', '#065f46'][i % 6]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => [formatCurrency(v), t('dashboard.revenue')]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <h3 className="products-analytics-section-title">{t('products.top10ByRevenue')}</h3>
              <div className="products-analytics-chart-box">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={analyticsData.topByRevenue.map((p) => ({ name: (p.productName || '').slice(0, 16), revenue: p.revenue }))}
                    layout="vertical"
                    margin={{ left: 20, right: 30 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} stroke="#6b7280" />
                    <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11 }} stroke="#6b7280" />
                    <Tooltip formatter={(v) => [formatCurrency(v), t('dashboard.revenue')]} />
                    <Bar dataKey="revenue" name={t('orders.revenueTzs')} fill="#039855" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <h3 className="products-analytics-section-title">{t('products.byCategory')}</h3>
              <div className="products-table-wrap">
                <table className="products-table">
                  <thead>
                    <tr>
                      <th>{t('products.category')}</th>
                      <th>{t('nav.products')}</th>
                      <th>{t('dashboard.unitsSold')}</th>
                      <th>{t('orders.revenueTzs')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsData.byCategory.map((c) => (
                      <tr key={c.name}>
                        <td><strong>{c.name}</strong></td>
                        <td>{c.count}</td>
                        <td>{formatCurrency(c.units)}</td>
                        <td>{formatCurrency(c.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {analyticsData.insights?.length > 0 && (
                <>
                  <h3 className="products-analytics-section-title">{t('dashboard.insights')}</h3>
                  <div className="products-analytics-insights">
                    {analyticsData.insights.map((item, i) => (
                      <div key={i} className={`products-analytics-insight products-analytics-insight-${item.type}`}>
                        <strong>{item.title}</strong>
                        {item.count != null && <span>{item.count}</span>}
                        {item.value != null && <span>{item.value}</span>}
                        — {item.action}
                      </div>
                    ))}
                  </div>
                </>
              )}

              <button
                type="button"
                className="products-analytics-refresh"
                onClick={loadAnalytics}
              >
                ⟳ {t('orders.refreshAnalytics')}
              </button>
            </>
          ) : (
            <p className="products-analytics-empty">{t('products.analyticsFailed')}</p>
          )}
        </div>
      )}

      {activeTab === 'list' && (
        <>
      <div className="products-toolbar">
        <div className="products-search-wrap">
          <img src="/icons/Search.svg" alt="" className="products-search-icon" aria-hidden />
          <input
            type="search"
            placeholder={t('products.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="products-search"
          />
        </div>
        <select
          value={filterStore}
          onChange={(e) => setFilterStore(e.target.value)}
          className="products-filter"
        >
          <option value="">{t('orders.allStores')}</option>
          {stores.map((s) => (
            <option key={s.$id} value={s.$id}>
              {s.storeName || s.$id}
            </option>
          ))}
        </select>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="products-filter"
        >
          <option value="">{t('products.allCategories')}</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      <div className="products-table-wrap">
        <table className="products-table">
          <thead>
            <tr>
              <th>{t('products.product')}</th>
              <th>{t('orders.store')}</th>
              <th>{t('products.category')}</th>
              <th>{t('products.price')}</th>
              <th>{t('products.stock')}</th>
              <th className="products-col-actions">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.length === 0 ? (
              <tr>
                <td colSpan={6} className="products-empty">
                  {t('products.noneFound')}
                </td>
              </tr>
            ) : (
              filteredProducts.map((p) => (
                <tr key={p.$id}>
                  <td>
                    <button
                      type="button"
                      className="products-link"
                      onClick={() => openView(p)}
                    >
                      {p.productName || t('products.unnamed')}
                    </button>
                  </td>
                  <td>{storeMap[p.storeId] || p.storeId || '—'}</td>
                  <td>{p.productCategory || '—'}</td>
                  <td>{formatCurrency(p.sellPrice)}</td>
                  <td>
                    <span
                      className={`products-stock ${
                        (parseInt(p.stockCount, 10) || 0) < 10 ? 'products-stock-low' : ''
                      }`}
                    >
                      {p.stockCount ?? '—'}
                    </span>
                  </td>
                  <td className="products-col-actions">
                    <button
                      type="button"
                      className="products-btn-sm"
                      onClick={() => openView(p)}
                      title={t('products.view')}
                    >
                      <img src="/icons/eye.svg" alt="" aria-hidden />
                      {t('products.view')}
                    </button>
                    {canUpdate && (
                      <button
                        type="button"
                        className="products-btn-sm products-btn-edit"
                        onClick={() => openEdit(p)}
                        title={t('common.edit')}
                      >
                        <img src="/icons/pencil-edit-02.svg" alt="" aria-hidden />
                        {t('common.edit')}
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        className="products-btn-sm products-btn-danger"
                        onClick={() => setDeleteConfirm(p)}
                        title={t('common.delete')}
                      >
                        <img src="/icons/delete-02.svg" alt="" aria-hidden />
                        {t('common.delete')}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {modal === 'edit' && (
        <div className="products-modal-overlay" onClick={closeModal}>
          <div
            className="products-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="products-modal-header">
              <h2>{t('products.editProduct')}</h2>
              <button type="button" className="products-modal-close" onClick={closeModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleSaveEdit} className="products-form">
              <div className="products-form-grid">
                <div className="products-form-field">
                  <label htmlFor="productName">{t('products.productNameRequired')}</label>
                  <input
                    id="productName"
                    required
                    value={formData.productName}
                    onChange={(e) =>
                      setFormData((f) => ({ ...f, productName: e.target.value }))
                    }
                  />
                </div>
                <div className="products-form-field">
                  <label htmlFor="productCategory">{t('products.category')}</label>
                  <input
                    id="productCategory"
                    value={formData.productCategory}
                    onChange={(e) =>
                      setFormData((f) => ({ ...f, productCategory: e.target.value }))
                    }
                    list="product-categories"
                  />
                  <datalist id="product-categories">
                    {categories.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </div>
                <div className="products-form-field">
                  <label htmlFor="sellPrice">{t('products.priceRequired')}</label>
                  <input
                    id="sellPrice"
                    type="number"
                    min="0"
                    required
                    value={formData.sellPrice}
                    onChange={(e) =>
                      setFormData((f) => ({ ...f, sellPrice: e.target.value }))
                    }
                  />
                </div>
                <div className="products-form-field">
                  <label htmlFor="stockCount">{t('products.stock')}</label>
                  <input
                    id="stockCount"
                    type="number"
                    min="0"
                    value={formData.stockCount}
                    onChange={(e) =>
                      setFormData((f) => ({ ...f, stockCount: e.target.value }))
                    }
                  />
                </div>
                <div className="products-form-field">
                  <label htmlFor="unit">{t('products.unit')}</label>
                  <input
                    id="unit"
                    value={formData.unit}
                    onChange={(e) =>
                      setFormData((f) => ({ ...f, unit: e.target.value }))
                    }
                    placeholder={t('products.unitPlaceholder')}
                  />
                </div>
              </div>
              <div className="products-modal-footer">
                <button
                  type="button"
                  className="products-btn-secondary"
                  onClick={closeModal}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="products-btn-primary"
                  disabled={saving}
                >
                  {saving ? t('products.saving') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div className="products-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div
            className="products-modal products-modal-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>{t('products.deleteConfirmTitle')}</h3>
            <p>
              {t('products.deleteConfirmBody', { name: deleteConfirm.productName })}
            </p>
            <div className="products-modal-footer">
              <button
                type="button"
                className="products-btn-secondary"
                onClick={() => setDeleteConfirm(null)}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="products-btn-danger"
                onClick={handleDelete}
                disabled={saving}
              >
                {saving ? t('products.deleting') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default ProductsPage;
